import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface ComplianceReport {
  reportDate: string;
  userCounts: {
    total: number;
    minors: number;
    adults: number;
    withConsent: number;
    withoutConsent: number;
  };
  dataRetention: {
    expiredData: number;
    retentionPeriods: { [key: string]: number };
  };
  deletionRequests: {
    pending: number;
    processed: number;
    scheduled: number;
  };
  internationalCompliance: {
    countryCounts: { [key: string]: number };
  };
  recommendations: Array<{
    priority: string;
    category: string;
    message: string;
    action: string;
  }>;
}

interface MinorUser {
  id: string;
  username: string;
  email: string;
  displayName: string;
  legalCompliance: {
    isMinor: boolean;
    parentalConsentGiven: boolean;
    coppaCompliant: boolean;
  };
  profile: {
    dateOfBirth: string;
  };
}

interface ExpiredDataUser {
  id: string;
  username: string;
  email: string;
  retentionPeriod: string;
  lastReviewDate: string;
}

const AdminCompliance: React.FC = () => {
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [minorUsers, setMinorUsers] = useState<MinorUser[]>([]);
  const [expiredUsers, setExpiredUsers] = useState<ExpiredDataUser[]>([]);
  const [usersWithoutConsent, setUsersWithoutConsent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      setLoading(true);

      // Fetch compliance report
      const reportResponse = await apiClient.get('/v1/admin/compliance/report');
      setReport(reportResponse.data);

      // Fetch minor users
      const minorsResponse = await apiClient.get('/v1/admin/users/minors');
      setMinorUsers(minorsResponse.data);

      // Fetch users without consent
      const consentResponse = await apiClient.get('/v1/admin/users/without-consent');
      setUsersWithoutConsent(consentResponse.data);

      // Fetch expired data users
      const expiredResponse = await apiClient.get('/v1/admin/compliance/expired-data');
      setExpiredUsers(expiredResponse.data);

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const runDataRetentionReview = async () => {
    try {
      const response = await apiClient.post('/v1/admin/compliance/review');
      const result = response.data;
      alert(`Data retention review completed. Reviewed: ${result.results.reviewed} users, Updated: ${result.results.updated} users`);
      
      // Refresh data
      await fetchComplianceData();
    } catch (error) {
      alert('Error running data retention review: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const anonymizeUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to anonymize this user\'s data? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiClient.post(`/v1/admin/users/${userId}/anonymize`);
      const result = response.data;
      alert(`User data anonymized successfully: ${result.user.username}`);
      
      // Refresh data
      await fetchComplianceData();
    } catch (error) {
      alert('Error anonymizing user data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birth = new Date(dateOfBirth);
    return Math.floor((today.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      default:
        return 'info';
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Compliance Management</h1>
          <p className="admin-page-description">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Compliance Management</h1>
          <p className="admin-page-description">Error loading compliance data</p>
        </div>
        <div className="admin-alert danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Compliance Management</h1>
        <p className="admin-page-description">
          Monitor and manage legal compliance, data retention, and privacy requirements
        </p>
      </div>

      {/* Action Buttons */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Actions</h2>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="admin-button" onClick={runDataRetentionReview}>
            Run Data Retention Review
          </button>
          <button className="admin-button secondary" onClick={fetchComplianceData}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-card">
        <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'minors', label: `Minors (${minorUsers.length})` },
              { id: 'consent', label: `No Consent (${usersWithoutConsent.length})` },
              { id: 'expired', label: `Expired Data (${expiredUsers.length})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid #1976d2' : '2px solid transparent',
                  color: activeTab === tab.id ? '#1976d2' : '#666',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab.id ? '600' : 'normal',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && report && (
          <div>
            {/* Recommendations */}
            {report.recommendations.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3>⚠️ Recommendations</h3>
                {report.recommendations.map((rec, index) => (
                  <div key={index} className={`admin-alert ${getPriorityColor(rec.priority)}`}>
                    <strong>{rec.category}:</strong> {rec.message}
                    <br />
                    <em>Action: {rec.action}</em>
                  </div>
                ))}
              </div>
            )}

            {/* Statistics Grid */}
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <div className="admin-stat-value">{report.userCounts.total}</div>
                <div className="admin-stat-label">Total Users</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{report.userCounts.minors}</div>
                <div className="admin-stat-label">Minor Users</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{report.userCounts.withoutConsent}</div>
                <div className="admin-stat-label">Without Consent</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{report.dataRetention.expiredData}</div>
                <div className="admin-stat-label">Expired Data</div>
              </div>
            </div>

            {/* Compliance Breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              <div>
                <h4>Data Retention Periods</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {Object.entries(report.dataRetention.retentionPeriods).map(([period, count]) => (
                    <li key={period} style={{ padding: '0.25rem 0' }}>
                      {period}: <strong>{count}</strong> users
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4>Deletion Requests</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li style={{ padding: '0.25rem 0' }}>
                    Pending: <strong>{report.deletionRequests.pending}</strong>
                  </li>
                  <li style={{ padding: '0.25rem 0' }}>
                    Processed: <strong>{report.deletionRequests.processed}</strong>
                  </li>
                  <li style={{ padding: '0.25rem 0' }}>
                    Scheduled: <strong>{report.deletionRequests.scheduled}</strong>
                  </li>
                </ul>
              </div>

              <div>
                <h4>By Country</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {Object.entries(report.internationalCompliance.countryCounts).map(([country, count]) => (
                    <li key={country} style={{ padding: '0.25rem 0' }}>
                      {country}: <strong>{count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Minors Tab */}
        {activeTab === 'minors' && (
          <div>
            <h3>Minor Users (COPPA Compliance)</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Age</th>
                    <th>Parental Consent</th>
                    <th>COPPA Compliant</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {minorUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div>
                          <strong>{user.displayName}</strong>
                          <br />
                          <small>@{user.username}</small>
                        </div>
                      </td>
                      <td>{calculateAge(user.profile.dateOfBirth)}</td>
                      <td>
                        {user.legalCompliance.parentalConsentGiven ? (
                          <span style={{ color: '#2e7d32' }}>✅ Given</span>
                        ) : (
                          <span style={{ color: '#d32f2f' }}>❌ Not Given</span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${user.legalCompliance.coppaCompliant ? 'active' : 'suspended'}`}>
                          {user.legalCompliance.coppaCompliant ? 'Compliant' : 'Non-Compliant'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="admin-button danger"
                          onClick={() => anonymizeUser(user.id)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Anonymize
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Consent Tab */}
        {activeTab === 'consent' && (
          <div>
            <h3>Users Without GDPR Consent</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Country</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersWithoutConsent.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div>
                          <strong>{user.displayName}</strong>
                          <br />
                          <small>@{user.username}</small>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.profile?.country || 'Unknown'}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        <button
                          className="admin-button danger"
                          onClick={() => anonymizeUser(user.id)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Anonymize
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Expired Data Tab */}
        {activeTab === 'expired' && (
          <div>
            <h3>Users with Expired Data Retention</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Retention Period</th>
                    <th>Last Review</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expiredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div>
                          <strong>{user.username}</strong>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.retentionPeriod}</td>
                      <td>{formatDate(user.lastReviewDate)}</td>
                      <td>
                        <button
                          className="admin-button danger"
                          onClick={() => anonymizeUser(user.id)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Anonymize
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCompliance;